/**
 * CommentsPanel.js
 * Comment thread for one activity or WBS node: list (oldest first) + composer
 * with @-mentions. Reused by the bottom-panel "Comments" tab (activities) and
 * the WBS comments dialog in UnifiedScheduleView.
 *
 * Composer visibility follows the two-axis RBAC: can(Action.COMMENT) is true
 * for any project role (view+) and super_admin; an admin without a membership
 * row correctly sees the thread not at all (the API 404s for them upstream).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Loader2, MessageSquare, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { parseUtcTimestamp } from "../../lib/date_format";
import api from "../../lib/api";
import { Action } from "../../lib/auth";
import { useAuth } from "../../contexts/AuthContext";
import { useProject } from "../../contexts/ProjectContext";
import MentionTextarea, { survivingMentions } from "./MentionTextarea";

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// formatDistanceToNow throws on null, so guard the parse: a malformed
// created_at must not crash the whole thread render.
const relativeTime = (value) => {
  const parsed = parseUtcTimestamp(value);
  return parsed ? formatDistanceToNow(parsed, { addSuffix: true }) : "";
};

/**
 * Split a comment body into segments, marking "@Name" runs for names present
 * in the comment's mentions array so they can be highlighted.
 */
export function mentionSegments(body, mentions) {
  const names = (mentions || []).map((m) => m.name).filter(Boolean);
  if (!names.length || !body) return [{ text: body || "", isMention: false }];
  // Longest first so "Jo Ann Smith" wins over "Jo".
  names.sort((a, b) => b.length - a.length);
  const re = new RegExp(`@(?:${names.map(escapeRegExp).join("|")})`, "g");
  const segments = [];
  let last = 0;
  for (const match of body.matchAll(re)) {
    if (match.index > last) segments.push({ text: body.slice(last, match.index), isMention: false });
    segments.push({ text: match[0], isMention: true });
    last = match.index + match[0].length;
  }
  if (last < body.length) segments.push({ text: body.slice(last), isMention: false });
  return segments;
}

const CommentsPanel = ({
  entityType,
  entityId,
  onCountChange = null,
  // Extra classes for the composer's action row. The bottom-panel host passes
  // right padding so the Post button clears the fixed AI widget (bottom-right,
  // z-50) that otherwise sits on top of it.
  composerActionsClassName = "",
}) => {
  const { user } = useAuth();
  const { activeProject, can } = useProject();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [mentions, setMentions] = useState([]); // [{user_id, name}]
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const canComment = can(Action.COMMENT);

  const fetchComments = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await api.get("/comments", {
        params: { entity_type: entityType, entity_id: entityId },
      });
      setComments(res.data?.comments || []);
    } catch (error) {
      console.error("Failed to load comments:", error);
      toast.error("Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    setBody("");
    setMentions([]);
    fetchComments();
  }, [fetchComments]);

  const reportCount = useCallback(
    (next) => {
      if (onCountChange) onCountChange(entityId, next.length);
    },
    [onCountChange, entityId]
  );

  const handlePost = async () => {
    const trimmed = body.trim();
    if (!trimmed || posting) return;
    setPosting(true);
    try {
      const res = await api.post("/comments", {
        entity_type: entityType,
        entity_id: entityId,
        body: trimmed,
        mention_user_ids: survivingMentions(trimmed, mentions).map((m) => m.user_id),
      });
      if (!res.data?.id) {
        throw new Error("Invalid comment response");
      }
      const next = [...comments, res.data];
      setComments(next);
      setBody("");
      setMentions([]);
      reportCount(next);
    } catch (error) {
      console.error("Failed to post comment:", error);
      toast.error("Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (commentId) => {
    setDeletingId(commentId);
    try {
      await api.delete(`/comments/${commentId}`);
      const next = comments.filter((c) => c.id !== commentId);
      setComments(next);
      reportCount(next);
    } catch (error) {
      if (error.response?.status === 404) {
        // Already deleted elsewhere (another tab/user) — the server agrees
        // it's gone, so reconcile the stale local row instead of erroring.
        const next = comments.filter((c) => c.id !== commentId);
        setComments(next);
        reportCount(next);
      } else {
        console.error("Failed to delete comment:", error);
        toast.error("Failed to delete comment");
      }
    } finally {
      setDeletingId(null);
    }
  };

  const canDelete = useMemo(
    () => (comment) =>
      user && (comment.author_id === user.id || user.role === "super_admin"),
    [user]
  );

  return (
    <div className="flex flex-col h-full px-5 py-3" data-testid="comments-panel">
      {/* Thread */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-400">
            <MessageSquare className="h-6 w-6 mb-2" />
            <p className="text-sm">No comments yet</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="group flex gap-3" data-testid={`comment-${comment.id}`}>
              <div className="h-7 w-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">
                {(comment.author_name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-slate-800">
                    {comment.author_name}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {relativeTime(comment.created_at)}
                  </span>
                  {canDelete(comment) && (
                    <button
                      className="ml-auto opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 transition-opacity"
                      title="Delete comment"
                      disabled={deletingId === comment.id}
                      onClick={() => handleDelete(comment.id)}
                      data-testid={`delete-comment-${comment.id}`}
                    >
                      {deletingId === comment.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5 text-red-400 hover:text-red-600" />
                      )}
                    </button>
                  )}
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                  {mentionSegments(comment.body, comment.mentions).map((seg, i) =>
                    seg.isMention ? (
                      <span key={i} className="text-blue-700 bg-blue-50 rounded px-0.5 font-medium">
                        {seg.text}
                      </span>
                    ) : (
                      <React.Fragment key={i}>{seg.text}</React.Fragment>
                    )
                  )}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Composer — hidden for users without COMMENT (e.g. admin, no project role) */}
      {canComment && (
        <div className="pt-3 mt-2 border-t border-slate-200 shrink-0">
          <MentionTextarea
            value={body}
            onChange={setBody}
            mentions={mentions}
            onMentionsChange={setMentions}
            projectId={activeProject?.id}
            disabled={posting}
            onSubmitShortcut={handlePost}
          />
          <div className={cn("flex justify-end mt-2", composerActionsClassName)}>
            <Button
              size="sm"
              onClick={handlePost}
              disabled={posting || !body.trim()}
              data-testid="post-comment-button"
            >
              {posting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              ) : (
                <Send className="h-3.5 w-3.5 mr-1.5" />
              )}
              Post
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentsPanel;
